import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  DollarSign, TrendingUp, Wrench, Package, Loader2, ArrowUpDown,
  ChevronUp, ChevronDown, BarChart3, Users,
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';

function fmt$(cents) {
  if (cents == null) return '—';
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtPct(v) { return v != null ? `${Number(v).toFixed(1)}%` : '—'; }

function defaultDateRange() {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth() - 2, 1);
  return { from: from.toISOString().slice(0, 10), to: now.toISOString().slice(0, 10) };
}

const ACCENT_JC = {
  blue: { strip: 'bg-blue-500', chip: 'bg-blue-50 text-blue-600' },
  amber: { strip: 'bg-amber-500', chip: 'bg-amber-50 text-amber-600' },
  indigo: { strip: 'bg-slate-500', chip: 'bg-slate-50 text-slate-600' },
  purple: { strip: 'bg-purple-500', chip: 'bg-purple-50 text-purple-600' },
  green: { strip: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-600' },
  teal: { strip: 'bg-teal-500', chip: 'bg-teal-50 text-teal-600' },
};

function KpiCard({ label, value, icon: Icon, sub, accent = 'blue' }) {
  const a = ACCENT_JC[accent] || ACCENT_JC.blue;
  return (
    <Card data-testid={`kpi-${label.replace(/\s+/g, '-').toLowerCase()}`} className="relative overflow-hidden">
      <div className={`absolute left-0 top-0 bottom-0 w-1 ${a.strip}`} />
      <CardContent className="pt-5 pb-4 px-5 pl-6 flex items-start justify-between">
        <div>
          <p className="text-xs text-muted-foreground mb-1">{label}</p>
          <p className="text-2xl font-semibold tracking-tight">{value}</p>
          {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
        </div>
        {Icon && (
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${a.chip}`}>
            <Icon className="w-4 h-4" />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SortHeader({ label, field, sortField, sortDir, onSort, className = '' }) {
  const active = sortField === field;
  return (
    <button onClick={() => onSort(field)} className={`flex items-center gap-1 text-xs font-medium ${className}`}>
      {label}
      {active ? (sortDir === 'asc' ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />)
        : <ArrowUpDown className="w-3 h-3 opacity-30" />}
    </button>
  );
}

export default function JobCostingReport() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(defaultDateRange);
  const [techFilter, setTechFilter] = useState('');
  const [custFilter, setCustFilter] = useState('');
  const [technicians, setTechnicians] = useState([]);
  const [sortField, setSortField] = useState('completed_at');
  const [sortDir, setSortDir] = useState('desc');
  const [scope, setScope] = useState('completed');

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = { scope };
      if (dateRange.from) params.start_date = dateRange.from;
      if (dateRange.to) params.end_date = dateRange.to;
      if (techFilter) params.technician_id = techFilter;
      if (custFilter) params.customer_id = custFilter;
      const res = await api.get('/reports/job-costing', { params });
      setData(res.data);

      // Extract unique technicians for filter
      const techs = new Map();
      (res.data.rows || []).forEach(r => {
        if (r.technician_id && r.technician_name !== '—') {
          techs.set(r.technician_id, r.technician_name);
        }
      });
      setTechnicians([...techs.entries()].map(([id, name]) => ({ id, name })));
    } catch {
      setData({ rows: [], summary: {} });
    } finally { setLoading(false); }
  }, [dateRange, techFilter, custFilter, scope]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  const sortedRows = React.useMemo(() => {
    if (!data?.rows) return [];
    return [...data.rows].sort((a, b) => {
      let av = a[sortField], bv = b[sortField];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'string') av = av.toLowerCase();
      if (typeof bv === 'string') bv = bv.toLowerCase();
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortField, sortDir]);

  const summary = data?.summary || {};

  return (
    <Layout>
      <div className="space-y-6" data-testid="job-costing-report">
        {/* Header */}
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="job-costing-title">
              <BarChart3 className="w-6 h-6" />
              Job Costing Report
            </h1>
            <p className="text-sm text-muted-foreground mt-1 hidden sm:block">
              Profitability breakdown across completed jobs
            </p>
          </div>
        </div>

        {/* Scope toggle + Filters */}
        <div className="space-y-3">
          {/* Row 1: Scope — full width */}
          <div className="inline-flex items-center rounded-lg border p-0.5 bg-muted/40 w-full sm:w-auto" data-testid="scope-toggle">
            {[
              { value: 'completed', label: 'Completed Jobs' },
              { value: 'scheduled', label: 'Scheduled Jobs' },
              { value: 'all', label: 'All Jobs' },
            ].map(s => (
              <button
                key={s.value}
                onClick={() => setScope(s.value)}
                className={`flex-1 sm:flex-initial px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                  scope === s.value
                    ? 'bg-background shadow-sm text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
                data-testid={`scope-${s.value}`}
              >
                {s.label}
              </button>
            ))}
          </div>
          {/* Row 2: Date range — 2-col grid */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">From</label>
              <Input
                type="date" value={dateRange.from}
                onChange={e => setDateRange(d => ({ ...d, from: e.target.value }))}
                className="w-full" data-testid="filter-start-date"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground mb-1 block">To</label>
              <Input
                type="date" value={dateRange.to}
                onChange={e => setDateRange(d => ({ ...d, to: e.target.value }))}
                className="w-full" data-testid="filter-end-date"
              />
            </div>
          </div>
          {/* Row 3: Technician — full width on mobile */}
          <div>
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Technician</label>
            <Select value={techFilter || '__all__'} onValueChange={v => setTechFilter(v === '__all__' ? '' : v)}>
              <SelectTrigger className="w-full sm:w-48" data-testid="filter-technician">
                <SelectValue placeholder="All Technicians" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All Technicians</SelectItem>
                {technicians.map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* KPI Summary */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <KpiCard label="Total Revenue" value={fmt$(summary.total_revenue_cents)} icon={DollarSign} sub={`${summary.job_count || 0} jobs`} accent="blue" />
          <KpiCard label="Labor Cost" value={fmt$(summary.total_labor_cost_cents)} icon={Wrench} accent="amber" />
          <KpiCard label="Material Cost" value={fmt$(summary.total_material_cost_cents)} icon={Package} accent="indigo" />
          <KpiCard label="Projected Commission" value={fmt$(summary.total_commission_projected_cents)} icon={Users} sub={summary.total_commission_earned_cents > 0 ? `${fmt$(summary.total_commission_earned_cents)} earned` : undefined} accent="purple" />
          <KpiCard label="Gross Profit" value={fmt$(summary.total_gross_profit_cents)} icon={TrendingUp} accent="green" />
          <KpiCard label="Average Margin" value={fmtPct(summary.average_margin_pct)} icon={BarChart3} accent="teal" />
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : sortedRows.length === 0 ? (
          <div className="border rounded-lg p-12 text-center">
            <BarChart3 className="w-10 h-10 mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">No completed jobs found for this period</p>
          </div>
        ) : (
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[140px]">
                    <SortHeader label="Job" field="job_number" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  </TableHead>
                  <TableHead className="min-w-[140px] hidden md:table-cell">
                    <SortHeader label="Customer" field="customer_name" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  </TableHead>
                  <TableHead className="min-w-[120px] hidden lg:table-cell">
                    <SortHeader label="Technician" field="technician_name" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  </TableHead>
                  <TableHead className="text-right w-28">
                    <SortHeader label="Revenue" field="revenue_cents" sortField={sortField} sortDir={sortDir} onSort={handleSort} className="justify-end" />
                  </TableHead>
                  <TableHead className="text-right w-28 hidden md:table-cell">
                    <SortHeader label="Labor" field="labor_cost_cents" sortField={sortField} sortDir={sortDir} onSort={handleSort} className="justify-end" />
                  </TableHead>
                  <TableHead className="text-right w-28 hidden md:table-cell">
                    <SortHeader label="Material" field="material_cost_cents" sortField={sortField} sortDir={sortDir} onSort={handleSort} className="justify-end" />
                  </TableHead>
                  <TableHead className="text-right w-28 hidden lg:table-cell">
                    <SortHeader label="Commission" field="commission_projected_cents" sortField={sortField} sortDir={sortDir} onSort={handleSort} className="justify-end" />
                  </TableHead>
                  <TableHead className="text-right w-28 hidden lg:table-cell">
                    <SortHeader label="Direct Cost" field="total_direct_cost_cents" sortField={sortField} sortDir={sortDir} onSort={handleSort} className="justify-end" />
                  </TableHead>
                  <TableHead className="text-right w-28">
                    <SortHeader label="Profit" field="gross_profit_cents" sortField={sortField} sortDir={sortDir} onSort={handleSort} className="justify-end" />
                  </TableHead>
                  <TableHead className="text-right w-20">
                    <SortHeader label="Margin" field="gross_margin_pct" sortField={sortField} sortDir={sortDir} onSort={handleSort} className="justify-end" />
                  </TableHead>
                  <TableHead className="w-28 hidden lg:table-cell">
                    <SortHeader label={scope === 'completed' ? 'Completed' : 'Created'} field={scope === 'completed' ? 'completed_at' : 'created_at'} sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedRows.map(row => (
                  <TableRow
                    key={row.job_id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => navigate(`/jobs/${row.job_id}`)}
                    data-testid={`job-row-${row.job_id}`}
                  >
                    <TableCell>
                      <span className="font-medium text-sm">{row.job_number || row.job_name || row.job_id?.slice(0, 8)}</span>
                      {row.job_name && row.job_number && (
                        <p className="text-xs text-muted-foreground truncate max-w-[130px]">{row.job_name}</p>
                      )}
                      {scope !== 'completed' && row.job_status && (
                        <Badge variant="outline" className="text-[9px] mt-0.5">{row.job_status}</Badge>
                      )}
                    </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <span className="text-sm">{row.customer_name}</span>
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <span className="text-sm">{row.technician_name}</span>
                    </TableCell>
                    <TableCell className="text-right text-sm font-medium">{fmt$(row.revenue_cents)}</TableCell>
                    <TableCell className="text-right text-sm hidden md:table-cell">{fmt$(row.labor_cost_cents)}</TableCell>
                    <TableCell className="text-right text-sm hidden md:table-cell">{fmt$(row.material_cost_cents)}</TableCell>
                    <TableCell className="text-right text-sm hidden lg:table-cell">
                      {row.commission_projected_cents > 0 ? fmt$(row.commission_projected_cents) : '—'}
                      {row.commission_earned_cents > 0 && (
                        <p className="text-[10px] text-green-600">{fmt$(row.commission_earned_cents)} earned</p>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm hidden lg:table-cell">{fmt$(row.total_direct_cost_cents)}</TableCell>
                    <TableCell className={`text-right text-sm font-medium ${row.gross_profit_cents >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                      {fmt$(row.gross_profit_cents)}
                    </TableCell>
                    <TableCell className={`text-right text-sm font-medium ${row.gross_margin_pct >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                      {fmtPct(row.gross_margin_pct)}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell">
                      <span className="text-xs text-muted-foreground">
                        {(scope === 'completed' ? row.completed_at : row.created_at)
                          ? new Date(scope === 'completed' ? row.completed_at : row.created_at).toLocaleDateString()
                          : '—'}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </Layout>
  );
}
